import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Portfolio, Product } from '@appcycle/contracts';
import { api, setApiContext } from './api';
import { useSession } from './session';

interface PortfolioState {
  portfolio: Portfolio | null;
  selectedProduct: Product | null;
  loading: boolean;
  error: string | null;
  version: number;
  selectProduct: (productId: string) => void;
  refresh: () => Promise<void>;
}

const PortfolioContext = createContext<PortfolioState | null>(null);

export function PortfolioProvider({ children }: { children: ReactNode }) {
  const { loading: sessionLoading, user } = useSession();
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const next = await api<Portfolio>('/api/v1/portfolio');
      if (!Array.isArray(next.organizations) || !Array.isArray(next.teams) || !Array.isArray(next.products) || !next.selected) {
        throw new Error('The product portfolio response is invalid.');
      }
      setPortfolio(next);
      setApiContext(next.selected.organizationId, next.selected.productId);
      const product = next.products.find((item) => item.id === next.selected.productId);
      if (product?.accentColor) document.documentElement.style.setProperty('--cycle-blue', product.accentColor);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The product portfolio could not be loaded.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (sessionLoading) return;
    void refresh();
  }, [sessionLoading, user?.id]);

  function selectProduct(productId: string) {
    const product = portfolio?.products.find((item) => item.id === productId);
    if (!product || product.id === portfolio?.selected.productId) return;
    setApiContext(product.organizationId, product.id);
    document.documentElement.style.setProperty('--cycle-blue', product.accentColor);
    setPortfolio((current) => current ? {
      ...current,
      selected: {
        organizationId: product.organizationId,
        teamId: product.teamId,
        productId: product.id,
      },
    } : current);
    setVersion((value) => value + 1);
  }

  const selectedProduct = portfolio?.products?.find((product) => product.id === portfolio.selected.productId) ?? null;
  const value = useMemo<PortfolioState>(() => ({
    portfolio,
    selectedProduct,
    loading,
    error,
    version,
    selectProduct,
    refresh,
  }), [error, loading, portfolio, selectedProduct, version]);

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

export function usePortfolio() {
  const value = useContext(PortfolioContext);
  if (!value) throw new Error('usePortfolio must be used inside PortfolioProvider');
  return value;
}

export function usePortfolioVersion() {
  return useContext(PortfolioContext)?.version ?? 0;
}
